// src/products/userInvites/UserInvitesApp.js
// Updated with role checking - users must be Creator or Support in ANY product to send invites

import React, { useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Send, Users, FileText } from 'lucide-react';
import useUserInvitesStore from './state/userInvitesStore';
import { useGlobalStore } from '../../state/globalStore';
import SharedHeader from '../../components/layout/SharedHeader';
import Sidebar from '../../components/layout/Sidebar';
import SendInvitePage from './pages/SendInvitePage';
import ManageInvitesPage from './pages/ManageInvitesPage';
// InviteSettingsPage removed - settings moved to AdminHQ (Super Admin only)
// import InviteSettingsPage from './pages/InviteSettingsPage';
import EmailTemplatesPage from './pages/EmailTemplatesPage';
import { AlertTriangle } from 'lucide-react';

const UserInvitesApp = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useGlobalStore();
  const {
    loading,
    canSendInvites,
    checkInvitePermission,
  } = useUserInvitesStore();

  useEffect(() => {
    if (user?.id) {
      checkInvitePermission(user.id);
    }
  }, [user, checkInvitePermission]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!canSendInvites) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-8 bg-white rounded-lg shadow">
          <AlertTriangle className="h-16 w-16 text-orange-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Restricted</h2>
          <p className="text-gray-600 mb-6">
            You need to be a <strong>Creator</strong> user in at least one product to send invitations.
            {/* Note: Support level is not yet functional - hidden from end users */}
          </p>
          <button
            onClick={() => navigate('/my-account/subscriptions')}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            View Subscriptions
          </button>
          <button
            onClick={() => navigate('/my-account/dashboard')}
            className="ml-3 inline-flex items-center px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Navigation items for User Invites
  // Note: Settings moved to AdminHQ - only Super Admins can configure invite settings
  const navItems = [
    { id: '/userinvites', icon: Send, label: 'Send Invite' },
    { id: '/userinvites/manage', icon: Users, label: 'Manage Invites' },
    { id: '/userinvites/templates', icon: FileText, label: 'Email Preview' }
  ];

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar
        items={navItems}
        activeItem={location.pathname}
        onItemClick={(itemId) => navigate(itemId)}
        productName="User Invites"
        user={user}
        userProfile={userProfile}
        onBackToProducts={() => navigate('/my-account/dashboard')}
      />
      <div className="flex-1 flex flex-col overflow-hidden">
        <SharedHeader 
          user={user}
          userProfile={userProfile}
          userRole={null}
          productName="User Invites"
        />
        <main className="flex-1 overflow-y-auto bg-white">
          <Routes>
            <Route 
              path="/" 
              element={<SendInvitePage user={user} />} 
            />
            <Route 
              path="/manage" 
              element={<ManageInvitesPage user={user} />} 
            />
            {/* Settings route removed - moved to AdminHQ → Settings → Invite Settings tab */}
            <Route 
              path="/templates" 
              element={<EmailTemplatesPage user={user} />} 
            />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default UserInvitesApp;